import { useCallback, useState } from "react";
import { Modal, Input, Upload, message } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { v4 as uuidv4 } from "uuid";
import { ApolloError } from "@apollo/client";
import { ArrowRight, Building, HardHat, Image as ImageIcon } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import { useFileUpload } from "hooks/useFileUpload";
import { useCompleteOrganizationOnboardingMutation } from "graphql/mutations/complete-organization-onboarding.generated";
import { useAuth } from "context/AuthContext";
import { OrganizationType } from "constants/field-values";
import { readAsDataUrl, uploadToStorage } from "utils/files-management";

const { Dragger } = Upload;

enum OnboardingStep {
	WELCOME = "welcome",
	ORGANIZATION_TYPE = "organization_type",
	ORGANIZATION_DETAILS = "organization_details"
}

const OLLIE_MASCOT_IMAGE = `${process.env.PUBLIC_URL}/assets/images/icons/ollie-owl.svg`;
const WELCOME_BANNER_BACKGROUND = `${process.env.PUBLIC_URL}/assets/images/layout/iris-forms-hero.png`;

// Mandatory, non-dismissable onboarding for a self-onboarded (basic role) user: pick an
// organization type, name it, optionally add a logo. Completing it promotes the user to
// vendor_manager/customer_manager and moves them out of the shared Free organization (MWA-838).
const OrganizationOnboardingModal = () => {
	const { refreshToken } = useAuth();
	const [step, setStep] = useState<OnboardingStep>(OnboardingStep.WELCOME);
	const [organizationType, setOrganizationType] = useState<OrganizationType | null>(null);
	const [organizationName, setOrganizationName] = useState("");

	const [logoThumbUrl, setLogoThumbUrl] = useState("");
	const [logoFile, setLogoFile] = useState<RcFile | null>(null);
	const [uploading, setUploading] = useState(false);
	const { convertHeicIfNeeded, isConverting } = useFileUpload();

	const [completeOrganizationOnboardingMutation, { loading: isSubmitting }] = useCompleteOrganizationOnboardingMutation(
		{
			errorPolicy: "none",
			fetchPolicy: "network-only"
		}
	);

	// Reads the dropped/selected file into the dropzone's preview and blocks antd from
	// uploading it itself — the real upload to storage happens on Complete Setup.
	const handleSelectLogo = useCallback(
		async (file: RcFile) => {
			const converted = ((await convertHeicIfNeeded(file)) as RcFile) ?? file;
			const thumbUrl = await readAsDataUrl(converted);
			setLogoFile(converted);
			setLogoThumbUrl(thumbUrl);
			return Upload.LIST_IGNORE;
		},
		[convertHeicIfNeeded]
	);

	const handleSubmit = useCallback(async () => {
		if (!organizationType || !organizationName.trim()) return;

		try {
			setUploading(!!logoFile);
			let organizationImageUrl: string | undefined;

			if (logoFile) {
				const uniqueId = uuidv4();
				const fileName = `organization-photo-${organizationName}-${uniqueId}-${Math.floor(Date.now() / 1000)}-${logoFile.name}`;
				const { publicUrl } = await uploadToStorage(logoFile, () => {}, `organization-photos/${fileName}`);
				organizationImageUrl = publicUrl;
			}

			await completeOrganizationOnboardingMutation({
				variables: {
					organization_type: organizationType,
					organization_name: organizationName.trim(),
					organization_image_url: organizationImageUrl
				}
			});

			if (refreshToken) {
				await refreshToken();
			}
		} catch (error) {
			const serverMessage = (error as ApolloError).graphQLErrors?.[0]?.message;
			message.error(serverMessage || "Your organization could not be created. Please try again.");
		} finally {
			setUploading(false);
		}
	}, [organizationType, organizationName, logoFile, completeOrganizationOnboardingMutation, refreshToken]);

	const isBusy = isSubmitting || uploading;

	const renderWelcome = () => (
		<div className="oom-welcome">
			<div className="oom-welcome-banner">
				<img src={WELCOME_BANNER_BACKGROUND} alt="" className="oom-welcome-banner-bg" />
				<div className="oom-welcome-avatar">
					<img src={OLLIE_MASCOT_IMAGE} alt="Ollie" className="oom-welcome-avatar-image" />
				</div>
			</div>
			<div className="oom-welcome-body">
				<h2 className="oom-title">Hi, I&apos;m Ollie. Welcome in!</h2>
				<p className="oom-welcome-subtitle">
					To get started, let&apos;s set up your organization. This helps us personalize your experience and allows you
					to invite other team members as we work to put your plan reviews and inspections in the Fast Lane!
				</p>
				<button type="button" className="oom-btn-primary" onClick={() => setStep(OnboardingStep.ORGANIZATION_TYPE)}>
					Get Started
					<ArrowRight className="oom-btn-icon" />
				</button>
			</div>
		</div>
	);

	const renderOrganizationType = () => (
		<div className="oom-organization-type">
			<div className="oom-step-header">
				<h2 className="oom-step-title">Organization Type</h2>
				<p className="oom-step-subtitle">How will you be using Ollie?</p>
			</div>
			<div className="oom-step-body">
				<button
					type="button"
					className={`oom-option-card ${organizationType === OrganizationType.Customer ? "oom-option-card-selected" : ""}`}
					onClick={() => setOrganizationType(OrganizationType.Customer)}
				>
					<span
						className={`oom-option-icon-badge ${organizationType === OrganizationType.Customer ? "" : "oom-option-icon-badge-neutral"}`}
					>
						<Building
							className={`oom-option-icon ${organizationType === OrganizationType.Customer ? "" : "oom-option-icon-neutral"}`}
						/>
					</span>
					<span className="oom-option-copy">
						<span className="oom-option-title">Property Owner or Developer</span>
						<span className="oom-option-description">
							We are using Ollie to expedite permits and inspections for our own commercial properties.
						</span>
					</span>
				</button>
				<button
					type="button"
					className={`oom-option-card ${organizationType === OrganizationType.Vendor ? "oom-option-card-selected" : ""}`}
					onClick={() => setOrganizationType(OrganizationType.Vendor)}
				>
					<span
						className={`oom-option-icon-badge ${organizationType === OrganizationType.Vendor ? "" : "oom-option-icon-badge-neutral"}`}
					>
						<HardHat
							className={`oom-option-icon ${organizationType === OrganizationType.Vendor ? "" : "oom-option-icon-neutral"}`}
						/>
					</span>
					<span className="oom-option-copy">
						<span className="oom-option-title">Contractor or Vendor</span>
						<span className="oom-option-description">
							We are a GC, subcontractor, or designer using Ollie to pre-screen our commercial projects before AHJ
							review or inspection.
						</span>
					</span>
				</button>
			</div>
			<div className="oom-step-footer">
				<button type="button" className="oom-btn-secondary" onClick={() => setStep(OnboardingStep.WELCOME)}>
					Back
				</button>
				<button
					type="button"
					className="oom-btn-primary oom-btn-primary-inline"
					disabled={!organizationType}
					onClick={() => setStep(OnboardingStep.ORGANIZATION_DETAILS)}
				>
					Continue
				</button>
			</div>
		</div>
	);

	const renderOrganizationDetails = () => (
		<div className="oom-organization-details">
			<div className="oom-step-header">
				<h2 className="oom-step-title">Organization Details</h2>
				<p className="oom-step-subtitle">Almost there! Tell us about your company.</p>
			</div>
			<div className="oom-step-body-details">
				<div className="oom-field">
					<label className="oom-field-label">Organization Name *</label>
					<Input
						className="oom-input"
						placeholder="e.g. Acme Construction Co."
						value={organizationName}
						onChange={e => setOrganizationName(e.target.value)}
						disabled={isBusy}
					/>
				</div>
				<div className="oom-field">
					<label className="oom-field-label">Organization Logo (Optional)</label>
					<Dragger
						name="file"
						multiple={false}
						showUploadList={false}
						accept="image/*"
						beforeUpload={handleSelectLogo}
						disabled={isBusy || isConverting}
						className={`oom-logo-dropzone ${logoThumbUrl ? "" : "oom-logo-dropzone-empty"}`}
					>
						{logoThumbUrl ? (
							<img src={logoThumbUrl} alt="" className="oom-logo-preview" />
						) : (
							<>
								<span className="oom-logo-dropzone-icon-badge">
									<ImageIcon className="oom-logo-dropzone-icon" />
								</span>
								<p className="oom-logo-dropzone-title">Click to upload or drag and drop</p>
							</>
						)}
					</Dragger>
				</div>
			</div>
			<div className="oom-step-footer">
				<button
					type="button"
					className="oom-btn-secondary"
					onClick={() => setStep(OnboardingStep.ORGANIZATION_TYPE)}
					disabled={isBusy}
				>
					Back
				</button>
				<button
					type="button"
					className="oom-btn-primary oom-btn-primary-inline"
					disabled={!organizationName.trim() || isBusy}
					onClick={handleSubmit}
				>
					Complete Setup
				</button>
			</div>
		</div>
	);

	return (
		<OllieLayout>
			<div className="oom-page">
				<Modal
					open
					footer={null}
					title={null}
					closable={false}
					maskClosable={false}
					centered
					width={step === OnboardingStep.WELCOME ? 578 : 573}
					className="oom-modal"
				>
					{step === OnboardingStep.WELCOME && renderWelcome()}
					{step === OnboardingStep.ORGANIZATION_TYPE && renderOrganizationType()}
					{step === OnboardingStep.ORGANIZATION_DETAILS && renderOrganizationDetails()}
				</Modal>
			</div>
		</OllieLayout>
	);
};

export default OrganizationOnboardingModal;
